<script setup lang="ts">
const { aposte, comunidade, regras, contactUrl } = useFooterMenu();

const { isDesktop } = useLayout();

const ACCORDION = {
  ATALHO: 'atalho',
  INFORMACOES: 'informacoes',
  SOBRE_NOS: 'sobre-nos',
} as const;

type AccordionKey = (typeof ACCORDION)[keyof typeof ACCORDION];

const openAccordion = ref<AccordionKey | null>(ACCORDION.SOBRE_NOS);

function isOpen(key: AccordionKey) {
  return openAccordion.value === key;
}

function setOpen(key: AccordionKey, value: boolean) {
  openAccordion.value = value ? key : null;
}
</script>

<template>
  <div
    class="footer-v2"
    data-testid="footer-v2-root"
  >
    <!-- SessionInfo (Fase A desktop / topo do container).
         Componente decide internamente se renderiza (apenas autenticados). -->
    <FooterV2SessionInfo />

    <FooterV2Accordion
      class="lg:hidden"
      :open="isOpen(ACCORDION.ATALHO)"
      title="Atalho"
      @update:open="setOpen(ACCORDION.ATALHO, $event)"
    >
      <div class="footer-v2__cols">
        <FooterV2MenuColumn :group="aposte" />
        <FooterV2MenuColumn :group="comunidade" />
      </div>
    </FooterV2Accordion>

    <FooterV2Accordion
      class="lg:hidden"
      :open="isOpen(ACCORDION.INFORMACOES)"
      title="Informações"
      @update:open="setOpen(ACCORDION.INFORMACOES, $event)"
    >
      <div class="footer-v2__cols">
        <FooterV2MenuColumn :group="regras" />
        <FooterV2LinksColumn :contact-url="contactUrl" />
      </div>
    </FooterV2Accordion>

    <FooterV2Accordion
      class="lg:hidden"
      :open="isOpen(ACCORDION.SOBRE_NOS)"
      title="Sobre nós"
      @update:open="setOpen(ACCORDION.SOBRE_NOS, $event)"
    >
      <FooterV2AboutContent />
    </FooterV2Accordion>

    <!-- Shell desktop: grid 3 colunas lado a lado (visível apenas em ≥1024px).
           Esquerda (281px): LogoSocial + AppStores empilhados.
           Centro       : 4 menus diretos (Aposte, Comunidade, Regras, Links).
           Direita (314px): AboutContent. -->
    <div class="footer-v2__shell-desktop hidden lg:flex">
      <!-- Coluna esquerda ------------------------------------------- -->
      <div class="footer-v2__col-left">
        <FooterV2LogoSocial />
        <FooterV2AppStores />
      </div>

      <!-- Coluna centro: 4 menus diretos (sem agrupamento extra) ---- -->
      <FooterV2MenuColumn :group="aposte" />
      <FooterV2MenuColumn :group="comunidade" />
      <FooterV2MenuColumn :group="regras" />
      <FooterV2LinksColumn :contact-url="contactUrl" />

      <!-- Coluna direita -------------------------------------------- -->
      <FooterV2AboutContent class="footer-v2__col-right" />
    </div>

    <!-- AuthorizedSeal full-width só mobile.
         No desktop ele aparece embutido dentro do SealsCompliance. -->
    <FooterV2AuthorizedSeal class="lg:hidden" />

    <!-- BottomBar: agrupa Patrocinadores + Selos.
         Mobile: flex-col (cada bloco em sua linha).
         Desktop: flex-row com gap 20, cada card com border+padding próprios. -->
    <div class="footer-v2__bottombar">
      <FooterV2Sponsors />
      <FooterV2SealsCompliance />
    </div>

    <FooterV2LogoSocial v-if="!isDesktop" />
    <FooterV2AppStores v-if="!isDesktop" />
    <FooterV2ComplianceBar />
    <FooterV2ComplianceButtons />
    <FooterV2Brazilians v-if="!isDesktop" />
    <FooterCasinoAuth />
  </div>
</template>

<style scoped lang="scss">
.footer-v2 {
  @apply w-full flex flex-col items-stretch;
  @apply p-5 gap-3;
  /* Importante: SEM max-width/mx-auto. O footer deve ocupar 100%
     da horizontal (edge-to-edge) no desktop. */
  @apply lg:p-[50px] lg:gap-5 lg:items-center;
  @apply lg:rounded-md;

  &__cols {
    @apply flex justify-between items-start self-stretch;
    padding: 0 16px;
    gap: 16px;
  }

  &__shell-desktop {
    /* Os 4 menus do centro são filhos diretos (sem sub-wrapper),
       então space-between distribui automaticamente:
         [LogoCol]  [Aposte] [Comunidade] [Regras] [Links]  [SobreCol] */
    @apply self-stretch items-start justify-between;
    gap: 16px;
  }

  &__col-left {
    /* Contém LogoSocial (logo + redes) + AppStores empilhados. */
    @apply flex flex-col items-start;
    width: 281px;
    gap: 18px;
    flex-shrink: 0;
  }

  &__col-right {
    /* AboutContent já traz padding/gap/typography próprios via lg:. */
    width: 314px;
    flex-shrink: 0;
  }

  /* Seletores legados zerados — mantidos caso algum estilo herdado
     ainda os referencie. */
  &__menus-desktop,
  &__menus-group-1,
  &__menus-group-2 {
    display: none;
  }

  &__bottombar {
    /* Mobile: stack vertical. */
    @apply flex flex-col items-stretch self-stretch;
    gap: 12px;

    /* Desktop: row, cards filhos cuidam dos próprios borders/paddings. */
    @media (min-width: 1024px) {
      flex-direction: row;
      align-items: flex-start;
      gap: 20px;
    }
  }
}
</style>
